import { ReportUseGroup } from "../../../interfaces/EstimateUseGroup";
import { getNamedRangeFromWorksheet } from "../../workbook";
import { columnNames, rowNames } from "./rangeNames";
import { writeCostDenominators } from "./costDenominators";
import { ValidationError } from "../../../errors";

// add a local fallback label to avoid circular deps
const FALLBACK_USE_GROUP_HEADER = "blank";

export type UseGroupColumnInfo = {
  [key: string]: { total: number; gsf?: number; unit?: number };
};

/**
 * Fills a use-group column block, one column per use group plus its cost-per columns when the sheet
 * has the cost denominators row, and returns where the columns landed. Shared by the SOV and
 * estimate reports.
 */
export const handleUseGroupsWithDenominators = async (
  worksheet: Excel.Worksheet,
  useGroups: ReportUseGroup[],
  dataRangeName: string,
  isSOV?: boolean,
  options?: {
    markupsRangeName?: string;
    subTotalsRangeNamePrefix?: string;
    grandTotalRangeName?: string;
    applyStyleToFirstRow?: boolean;
    /** Column block to fill; defaults to `\use_groups`. */
    columnRangeName?: string;
  }
): Promise<
  | {
      useGroupColumnIndices: UseGroupColumnInfo;
      costDenominatorsRowIndex?: number;
      firstMarkupRowIndex?: number;
      firstRowIndex: number;
      useGroupsRequested: boolean;
      costPerColumnsRequested: boolean;
    }
  | undefined
> => {
  const columnRangeName = `\\${options?.columnRangeName ?? columnNames.useGroups}`;
  const firstUseGroupColumn = await getNamedRangeFromWorksheet(worksheet, columnRangeName);
  if (!firstUseGroupColumn) {
    return undefined;
  }

  const dataRange = await getNamedRangeFromWorksheet(worksheet, dataRangeName);
  if (!dataRange) {
    throw new ValidationError("The worksheet does not contain the required named range", { rangeName: dataRangeName });
  }

  const costDenominatorsRowRange = await getNamedRangeFromWorksheet(worksheet, rowNames.costDenominators);
  const markupDataRange = options?.markupsRangeName
    ? await getNamedRangeFromWorksheet(worksheet, options.markupsRangeName)
    : null;

  firstUseGroupColumn.getEntireColumn().ungroup("ByColumns");

  const useGroupDenominatorsRequested = costDenominatorsRowRange !== null;

  const firstRowRange = dataRange.getCell(0, 0);
  const firstMarkupRowRange = markupDataRange?.getCell(0, 0);
  firstRowRange.load("rowIndex");
  costDenominatorsRowRange?.load("rowIndex");
  firstUseGroupColumn.load("columnCount, address, columnIndex");
  firstMarkupRowRange?.load("rowIndex");
  await worksheet.context.sync();

  const firstRowIndex = firstRowRange.rowIndex;
  const costDenominatorsRowIndex = costDenominatorsRowRange?.rowIndex;
  const firstMarkupRowIndex = firstMarkupRowRange?.rowIndex;

  // Compute the display column count and denominator column metadata
  let displayColumns = useGroups.length; // at least the total column per group
  const denomByRelativeIndex: { [relativeColIndex: number]: { value: string; uom: string } } = {};
  let relativeCursor = 0; // relative column index within the range

  if (useGroups.length > 0 && useGroupDenominatorsRequested) {
    for (const ug of useGroups) {
      // total column (advance cursor, but no denominator metadata)
      relativeCursor += 1;

      if (ug.gsf && ug.gsf > 0) {
        denomByRelativeIndex[relativeCursor] = {
          value: String(ug.gsf),
          uom: "GSF",
        };
        relativeCursor += 1;
        displayColumns += 1;
      }
      if (ug.unit_count && ug.unit_count > 0) {
        denomByRelativeIndex[relativeCursor] = {
          value: String(ug.unit_count),
          uom: ug.unit_cost_label || "unit",
        };
        relativeCursor += 1;
        displayColumns += 1;
      }
    }
  }

  // Always keep at least one column to avoid negative resizing
  displayColumns = Math.max(1, displayColumns);

  // Resize columns to match displayColumns
  const existingColumns = firstUseGroupColumn.columnCount;
  const startCell = firstUseGroupColumn.getCell(0, 0);
  startCell.format.load("columnWidth");
  startCell.load("columnIndex");
  await worksheet.context.sync();

  if (existingColumns > 1) {
    const toDelete = startCell.getOffsetRange(0, 1).getResizedRange(0, existingColumns - 2);
    toDelete.getEntireColumn().delete(Excel.DeleteShiftDirection.left);
    await worksheet.context.sync();
  }

  if (displayColumns > 1) {
    let newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.insert(Excel.InsertShiftDirection.right);
    await worksheet.context.sync();

    newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.copyFrom(startCell.getEntireColumn(), Excel.RangeCopyType.all);
    newCols.format.columnWidth = startCell.format.columnWidth;
  }
  await worksheet.context.sync();

  const updatedRange = startCell.getResizedRange(0, displayColumns - 1);

  // Replace the named range with the resized range
  worksheet.names.getItemOrNullObject(columnRangeName).delete();
  await worksheet.context.sync();
  worksheet.names.add(columnRangeName, updatedRange);

  // Header row
  const headers =
    useGroups.length > 0
      ? useGroups.flatMap((ug) => {
          const a = [ug.name];
          if (useGroupDenominatorsRequested) {
            if (ug.gsf && ug.gsf > 0) a.push("$/SF");
            if (ug.unit_count && ug.unit_count > 0) a.push(`$/${ug.unit_cost_label || "unit"}`);
          }
          return a;
        })
      : [FALLBACK_USE_GROUP_HEADER];

  updatedRange.values = [headers];
  await worksheet.context.sync();

  // Clear contents of use groups column when no use groups are present to remove #REF errors
  if (useGroups.length === 0) {
    updatedRange.getEntireColumn().clear(Excel.ClearApplyTo.contents);
    await worksheet.context.sync();
  }

  // Build column indices mapping
  const useGroupColumnIndices: UseGroupColumnInfo = {};
  if (useGroups.length > 0) {
    let columnIndex = startCell.columnIndex;
    useGroups.forEach((useGroup) => {
      const useGroupTotalCol = columnIndex++;
      let gsfCol: number | undefined;
      let unitCol: number | undefined;

      if (useGroup.gsf && useGroup.gsf > 0 && useGroupDenominatorsRequested) {
        gsfCol = columnIndex++;
      }
      if (useGroup.unit_count && useGroup.unit_count > 0 && useGroupDenominatorsRequested) {
        unitCol = columnIndex++;
      }

      useGroupColumnIndices[useGroup.id] = { total: useGroupTotalCol, gsf: gsfCol, unit: unitCol };
    });
  }

  if (useGroups.length > 0 && useGroupDenominatorsRequested && costDenominatorsRowIndex !== undefined) {
    await writeCostDenominators({
      worksheet,
      columns: Object.keys(denomByRelativeIndex)
        .map(Number)
        .map((k) => ({ columnIndex: startCell.columnIndex + k, ...denomByRelativeIndex[k] })),
      costDenominatorsRowIndex,
      firstRowIndex,
      firstMarkupRowIndex,
      subTotalsRangeNamePrefix: options?.subTotalsRangeNamePrefix,
      grandTotalRangeName: options?.grandTotalRangeName,
      applyStyleToFirstRow: options?.applyStyleToFirstRow !== false,
      isSOV: !!isSOV,
    });
  }

  updatedRange.getEntireColumn().group("ByColumns");
  await worksheet.context.sync();

  return {
    useGroupColumnIndices,
    costDenominatorsRowIndex,
    firstMarkupRowIndex,
    firstRowIndex,
    useGroupsRequested: true,
    costPerColumnsRequested: useGroupDenominatorsRequested,
  };
};
